import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Agde' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Agde',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Agde' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Agde. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous au Cap d\'Agde pour les résidences secondaires ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons au Cap d\'Agde et dans toutes les résidences du littoral agathois. Nous savons que beaucoup de propriétaires ne sont pas sur place en permanence : nous pouvons intervenir en leur absence après accord, ou nous adapter à leurs dates de présence.',
      },
    },
    {
      '@type': 'Question',
      name: 'Les menuiseries du littoral agathois nécessitent-elles un entretien particulier ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, l\'air salin et le vent accélèrent l\'usure. Nous recommandons un entretien annuel : nettoyage des rails de baie, lubrification des roulettes et vérification des joints. Nos techniciens peuvent programmer ces visites préventives et vous alerter avant qu\'une panne ne survienne.',
      },
    },
  ],
};

export default function AgdePage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Agde | DFPRO"
        description="Baie coulissante ou volet roulant en panne à Agde et au Cap d'Agde ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Pièces anticorrosion."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-agde/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Agde"
        subtitle="Du centre ancien au Cap d'Agde, nos techniciens réparent vos baies coulissantes, volets roulants et vitrages exposés au sel et au vent. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie coulissante ou volet en panne à Agde ?"
        ctaText="Devis gratuit, pièces traitées anticorrosion pour le littoral, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Agde, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Agde, l'air salin et le sable attaquent les menuiseries en permanence. Baie coulissante qui coince au Cap, volet roulant bloqué après une tempête, vitrage embué dans une résidence secondaire : nos techniciens utilisent des pièces anticorrosion. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/agde-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Agde"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante rongée par le sable et le sel</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les baies vitrées coulissantes sont omniprésentes au Cap d'Agde. Le sable et le sel s'infiltrent dans les rails, les roulettes grippent, la baie devient dure à manœuvrer puis <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince complètement</Link>. Ne forcez pas : vous risquez d'aggraver les dégâts.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous remplaçons les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes</Link> par des modèles anticorrosion, réparons le <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link> et effectuons le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>. Si l'usure est trop avancée, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link> complet avec double vitrage.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : pannes aggravées par le vent du large</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les rafales qui balaient le littoral agathois mettent les volets roulants à l'épreuve. <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Volet bloqué</Link> après une tempête, <Link to="/volet-roulant-ne-remonte-plus-herault/" className="text-accent-500 font-semibold hover:underline">volet qui ne remonte plus</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur grillé</Link> : notre équipe diagnostique et répare sur place.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous assurons aussi la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> de volets manuels avec des moteurs protégés. Particulièrement demandé par les propriétaires de résidences secondaires au Cap.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/agde-volet-02.jpg"
              alt="Réparation de volet roulant à Agde"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/agde-vitrage-03.webp"
              alt="Remplacement de vitrage à Agde"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier en urgence au Cap d'Agde</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Vitre brisée dans une location saisonnière, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage embué</Link> dans une résidence en front de mer : notre service <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">vitrier en urgence</Link> sécurise votre logement sous 24h.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous intervenons aussi sur les <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres</Link> : <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> dégradés par le sel, <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link> grippées, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link> à régler.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Agde sous 24 à 48h</h2>
        <p>
          Nous couvrons tout le territoire agathois : centre historique, Cap d'Agde, le Grau d'Agde, la Tamarissière et zones résidentielles. Également Vias, Marseillan, Portiragnes. Retrouvez <Link to="/depannage-menuiserie-sete/" className="text-accent-500 font-semibold hover:underline">Sète</Link> et <Link to="/depannage-menuiserie-beziers/" className="text-accent-500 font-semibold hover:underline">Béziers</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}